import type { BusinessInfo } from '@/components/BusinessConfirmation'
import { BusinessFormFlow } from '@/components/BusinessFormFlow'
import { RocketIcon } from '@/components/icons/RocketIcon'
import { orpc } from '@/lib/orpc/client'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { useState } from 'react'

export const Route = createFileRoute('/_dashboard/create-business/')({
  component: CreateBusinessPage,
})

function CreateBusinessPage() {
  const createBusinessMutation = useMutation(
    orpc.business.createBusiness.mutationOptions(),
  )

  const [businessInfo, setBusinessInfo] = useState<BusinessInfo | null>(null)
  const [showSuccess, setShowSuccess] = useState(false)

  const handleBusinessSuccess = async (info: BusinessInfo) => {
    setBusinessInfo(info)
    try {
      await createBusinessMutation.mutateAsync(info)
      setShowSuccess(true)
    } catch (error) {
      console.error('Error creating business:', error)
    }
  }

  return (
    <div className="pt-20 mx-auto max-w-2xl mb-20">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-12 text-center"
      >
        <div className="flex justify-center mb-6">
          <motion.div
            animate={{
              rotate: [0, 10, -10, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            <RocketIcon className="w-16 h-16 text-blue-600" />
          </motion.div>
        </div>
        <h1 className="mb-4 text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
          Launch Your Business
        </h1>
        <p className="mx-auto max-w-md text-xl text-gray-600">
          Enter your business website and watch the magic happen. We'll help you
          get started in seconds.
        </p>
      </motion.div>

      {!showSuccess ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <BusinessFormFlow
            onSuccess={handleBusinessSuccess}
            showManualEntry={true}
          />
        </motion.div>
      ) : (
        businessInfo && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="p-4 text-center bg-green-50 rounded-lg border border-green-200"
          >
            <div className="mb-2 font-semibold text-green-600">
              🎉 Business Created Successfully!
            </div>
            <div className="text-green-700">
              Your business "{businessInfo.businessName}" is now ready to go!
            </div>
          </motion.div>
        )
      )}
    </div>
  )
}
